import {
  Box,
  Typography,
  useTheme,
  Divider,
} from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import ModalBase from "../../components/ModalBase";
import CustomButton from "../../components/CustomButton";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import { makeStyles } from "@mui/styles";
import { GlobalVars } from "../../global";
import { Network } from "../../services/api/Network";

interface AdminToolsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminToolsModal: FC<AdminToolsModalProps> = ({ isOpen, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isClearingCache, setIsClearingCache] = useState<boolean>(false);

  const handleClearAllGoogleReviewsCache = async () => {
    try {
      setIsClearingCache(true);
      const response = await Network.fetch(
        `${GlobalVars.SERVER_ENDPOINT}/google-reviews/cache/all`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      if (response && response.ok) {
        const result = await response.json();
        showFlashMessageWithTimeout(
          dispatch,
          t("admin.tools.clearGoogleReviewsCache.success") ||
            result.message ||
            "Cache cleared successfully",
          "success",
        );
      } else {
        throw new Error("Failed to clear cache");
      }
    } catch (error: any) {
      showFlashMessageWithTimeout(
        dispatch,
        t("admin.tools.error") || error.toString(),
        "error",
      );
    } finally {
      setIsClearingCache(false);
    }
  };

  return (
    <ModalBase isOpen={isOpen} onClose={onClose} center>
      <Box className={classes.container}>
        {/* Header */}
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {t("admin.tools.modalTitle") || "Admin Tools"}
          </Typography>
          <CloseIcon
            style={{ color: theme.palette.natural.t2, cursor: "pointer" }}
            onClick={onClose}
          />
        </Box>

        <Divider style={{ marginBottom: 24 }} />

        {/* Tools List */}
        <Box className={classes.toolsList}>
          {/* Google Reviews Cache Tool */}
          <Box className={classes.toolItem} sx={{ bgcolor: "white !important" }}>
            <Box className={classes.toolIcon} sx={{ justifyContent: 'center !important', alignItems: 'center !important', display: 'flex !important' }}>
              <DeleteOutlineIcon
                style={{ color: theme.palette.main.primary.o, fontSize: 28 }}
              />
            </Box>
            <Box className={classes.toolContent}>
              <Typography className={classes.toolTitle}>
                {t("admin.tools.clearGoogleReviewsCache.title")}
              </Typography>
              <Typography className={classes.toolDescription}>
                {t("admin.tools.clearGoogleReviewsCache.description")}
              </Typography>
              <Box className={classes.toolAction}>
                <CustomButton
                  isLoading={isClearingCache}
                  title={t("admin.tools.clearCache")}
                  onClick={handleClearAllGoogleReviewsCache}
                  size='small'
                  style={{ minWidth: 140 }}
                />
              </Box>
            </Box>
          </Box>

          {/* Add more admin tools here in the future */}
        </Box>

        {/* Footer */}
        <Box className={classes.footer}>
          <CustomButton
            title={t("close")}
            type='secondary'
            onClick={onClose}
            style={{ width: 120 }}
          />
        </Box>
      </Box>
    </ModalBase>
  );
};

const useStyles = makeStyles((theme: any) => ({
  container: {
    backgroundColor: theme.palette.natural.t6,
    borderRadius: 12,
    padding: "32px",
    minWidth: 600,
    maxWidth: 700,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: theme.palette.main.secondary.u1,
  },
  toolsList: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    marginBottom: 32,
  },
  toolItem: {
    display: "flex",
    gap: 16,
    padding: 20,
    backgroundColor: theme.palette.natural.t,
    borderRadius: 8,
    border: `1px solid ${theme.palette.natural.t4}`,
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
      transform: "translateY(-2px)",
    },
  },
  toolIcon: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    width: 48,
    height: 48,
    backgroundColor: theme.palette.main.primary.d3,
    borderRadius: 8,
    flexShrink: 0,
  },
  toolContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  toolTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.natural.t1,
  },
  toolDescription: {
    fontSize: 14,
    color: theme.palette.natural.t2,
    lineHeight: 1.5,
  },
  toolAction: {
    marginTop: 8,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: 16,
    borderTop: `1px solid ${theme.palette.natural.t4}`,
  },
}));

export default AdminToolsModal;
